import { Injectable } from '@angular/core';
import { ExerciseSet } from '../models/exercise-set.interface';

@Injectable({
  providedIn: 'root',
})
export class FormInputService {
  constructor() {}

  /**
   * Update time field from separate minutes and seconds fields
   * @param set - Exercise set object
   */
  updateTimeFromSeparateFields(set: ExerciseSet): void {
    const minutes = set.timeMinutes || 0;
    const seconds = set.timeSeconds || 0;

    // Create MM:SS format string
    set.time = `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }

  /**
   * Validate minutes input and update set
   */
  validateMinutesInput(event: any, set: ExerciseSet): void {
    const value = parseInt(event.target.value);
    if (isNaN(value) || value < 0) {
      set.timeMinutes = 0;
      event.target.value = 0;
    } else if (value > 99) {
      set.timeMinutes = 99;
      event.target.value = 99;
    } else {
      set.timeMinutes = value;
    }
    this.updateTimeFromSeparateFields(set);
  }

  /**
   * Validate seconds input and update set
   */
  validateSecondsInput(event: any, set: ExerciseSet): void {
    const value = parseInt(event.target.value);
    if (isNaN(value) || value < 0) {
      set.timeSeconds = 0;
      event.target.value = 0;
    } else if (value > 59) {
      set.timeSeconds = 59;
      event.target.value = 59;
    } else {
      set.timeSeconds = value;
    }
    this.updateTimeFromSeparateFields(set);
  }

  /**
   * Parse existing MM:SS time string into separate minutes and seconds fields
   * @param set - Exercise set object
   */
  parseSeparateTimeFields(set: ExerciseSet): void {
    if (set.time && set.time.includes(':')) {
      const parts = set.time.split(':');
      set.timeMinutes = parseInt(parts[0]) || 0;
      set.timeSeconds = parseInt(parts[1]) || 0;
    } else {
      set.timeMinutes = 0;
      set.timeSeconds = 0;
    }
  }

  /**
   * Enforce time format for input fields (legacy method for backward compatibility)
   * @param event - Input event
   * @param set - Exercise set to update
   */
  enforceTimeFormat(event: any, set: ExerciseSet): void {
    let value = event.target.value;

    // Remove any non-digit or non-colon characters
    value = value.replace(/[^0-9:]/g, '');

    // Limit to 5 characters (MM:SS)
    if (value.length > 5) {
      value = value.substring(0, 5);
    }

    // Auto-insert colon after 2 digits
    if (value.length === 2 && !value.includes(':')) {
      value = value + ':';
    }

    // Ensure proper format: MM:SS
    if (value.length >= 3) {
      const parts = value.split(':');
      if (parts.length >= 2) {
        let minutes = parts[0].padStart(2, '0').substring(0, 2);
        let seconds = parts[1].substring(0, 2);

        // Validate minutes (00-99)
        if (parseInt(minutes) > 99) {
          minutes = '99';
        }

        // Validate seconds (00-59)
        if (parseInt(seconds) > 59) {
          seconds = '59';
        }

        value = minutes + ':' + seconds;
      }
    }

    // Update the input value and the model
    event.target.value = value;
    set.time = value;
  }

  /**
   * Validate time format and apply strict formatting
   * @param event - Input event
   * @param set - Exercise set to update
   */
  validateTimeFormat(event: any, set: ExerciseSet): void {
    const timeValue = event.target.value.trim();
    if (timeValue && timeValue !== '') {
      const formattedTime = this.formatTimeInputStrict(timeValue);
      set.time = formattedTime;
      event.target.value = formattedTime;
    }
  }

  /**
   * Format time input with strict validation
   * @param timeValue - Input time value
   * @returns Formatted time string
   */
  formatTimeInputStrict(timeValue: string): string {
    // Remove any non-digit or non-colon characters
    timeValue = timeValue.replace(/[^0-9:]/g, '');

    // If no colon, add it based on length
    if (!timeValue.includes(':')) {
      if (timeValue.length === 1) {
        return '00:0' + timeValue;
      } else if (timeValue.length === 2) {
        const num = parseInt(timeValue);
        if (num <= 59) {
          return '00:' + timeValue;
        } else {
          return '0' + timeValue[0] + ':' + timeValue[1] + '0';
        }
      } else if (timeValue.length === 3) {
        return '0' + timeValue[0] + ':' + timeValue.substring(1);
      } else if (timeValue.length >= 4) {
        return timeValue.substring(0, 2) + ':' + timeValue.substring(2, 4);
      }
    }

    // Handle MM:SS format
    const parts = timeValue.split(':');
    if (parts.length === 2) {
      let minutes = parseInt(parts[0]) || 0;
      let seconds = parseInt(parts[1]) || 0;

      // Validate and constrain values - Updated for MM:SS format
      minutes = Math.min(Math.max(minutes, 0), 99); // Max 99 minutes
      seconds = Math.min(Math.max(seconds, 0), 59); // Max 59 seconds

      return `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    }

    return '00:00';
  }

  /**
   * Validate numeric input for reps and weights
   * @param event - Input event
   * @param field - Field name ('reps' or 'weights')
   * @param set - Exercise set to update
   * @param maxValue - Maximum allowed value (optional)
   */
  validateNumericInput(
    event: any,
    field: 'reps' | 'weights',
    set: ExerciseSet,
    maxValue?: number
  ): void {
    let value = event.target.value;

    // Remove any non-numeric characters except decimal point for weights
    if (field === 'weights') {
      value = value.replace(/[^0-9.]/g, '');

      // Ensure only one decimal point
      const decimalIndex = value.indexOf('.');
      if (decimalIndex !== -1) {
        value =
          value.substring(0, decimalIndex + 1) +
          value.substring(decimalIndex + 1).replace(/\./g, '');
      }
    } else {
      value = value.replace(/[^0-9]/g, '');
    }

    // Apply maximum value if specified
    if (maxValue !== undefined) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue) && numericValue > maxValue) {
        value = maxValue.toString();
      }
    }

    // Update the input value and the model
    event.target.value = value;
    set[field] = value;
  }

  /**
   * Format weight input to ensure proper decimal places
   * @param event - Input event
   * @param set - Exercise set to update
   * @param maxDecimalPlaces - Maximum decimal places allowed
   */
  formatWeightInput(
    event: any,
    set: ExerciseSet,
    maxDecimalPlaces: number = 2
  ): void {
    let value = event.target.value;
    const numericValue = parseFloat(value);

    if (!isNaN(numericValue)) {
      value = numericValue.toFixed(maxDecimalPlaces);
      // Remove trailing zeros and decimal point if not needed
      value = parseFloat(value).toString();
    }

    event.target.value = value;
    set.weights = value;
  }

  /**
   * Validate and format duration input
   * @param value - Duration value
   * @param minValue - Minimum allowed value
   * @param maxValue - Maximum allowed value
   * @returns Validated and formatted duration
   */
  validateDurationInput(
    value: any,
    minValue: number = 1,
    maxValue: number = 52
  ): number {
    const numericValue = parseInt(value, 10);
    if (isNaN(numericValue) || numericValue < minValue) {
      return minValue;
    }
    if (numericValue > maxValue) {
      return maxValue;
    }
    return numericValue;
  }

  /**
   * Sanitize text input by removing potentially harmful characters
   * @param value - Input value to sanitize
   * @returns Sanitized value
   */
  sanitizeTextInput(value: string): string {
    if (!value || typeof value !== 'string') {
      return '';
    }

    // Remove HTML tags and normalize whitespace
    return value
      .replace(/<[^>]*>/g, '')
      .trim()
      .replace(/\s+/g, ' ');
  }

  /**
   * Validate exercise name input
   * @param value - Exercise name to validate
   * @param maxLength - Maximum length allowed
   * @returns Validated exercise name
   */
  validateExerciseName(value: string, maxLength: number = 100): string {
    const sanitized = this.sanitizeTextInput(value);
    return sanitized.length > maxLength
      ? sanitized.substring(0, maxLength)
      : sanitized;
  }

  /**
   * Validate plan name and description inputs
   * @param value - Plan name or description to validate
   * @param maxLength - Maximum length allowed
   * @returns Validated input
   */
  validatePlanText(value: string, maxLength: number = 200): string {
    const sanitized = this.sanitizeTextInput(value);
    return sanitized.length > maxLength
      ? sanitized.substring(0, maxLength)
      : sanitized;
  }
}
